/**
 * SmartUpsellPanel — premium recommendation surface for the Upsell Intelligence Engine.
 *
 * Used in TWO locations:
 *   - <UpsellOpportunities> (/upsells/opportunities) → the full panel, for a
 *     project picked on that page. It used to sit on every project page; it was
 *     moved off because that page is for running a project, not selling into it.
 *   - <AddChangeOrderModal> Step 2 (Upsell) → above the catalog as a "Smart Opportunities" rail
 *
 * Driven by `/api/upsell-intelligence/{recommendations|foundations|pitch}`.
 */
import { useEffect, useMemo, useState } from "react";
import { apiClient } from "../App";
import { toast } from "sonner";
import {
  Sparkles, TrendingUp, AlertTriangle, Layers, ChevronDown, ChevronUp,
  Wand2, FileText, DollarSign, Clock, ShieldCheck, CheckCircle2, X, Copy,
} from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export function SmartUpsellPanel({ programId, programName, compact = false, onAddToProposal }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFoundations, setShowFoundations] = useState(false);

  useEffect(() => {
    if (!programId) return;
    setLoading(true);
    apiClient.get(`/upsell-intelligence/recommendations/${programId}`)
      .then(r => { setData(r.data); setError(null); })
      .catch(e => setError(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [programId]);

  const foundationLabels = useMemo(() => ({
    exterior_model_exists:           "Exterior model",
    interior_model_exists:           "Interior model",
    approved_still_renders_exist:    "Approved still renders",
    exterior_renders_exist:          "Exterior renders",
    interior_renders_exist:          "Interior renders",
    aerial_renders_exist:            "Aerial / drone",
    tour_environment_exists:         "360 environment",
    animation_exists:                "Animation",
    floorplans_exist:                "Floorplans",
    inventory_data_exists:           "Inventory data",
    vexos_exists:                    "VEXOS platform",
    branding_exists:                 "Branding / collateral",
    landing_page_exists:             "Landing page",
    campaign_assets_exist:           "Campaign assets",
  }), []);

  const activeFoundations = useMemo(() => {
    if (!data?.foundations?.flags) return [];
    return Object.entries(data.foundations.flags)
      .filter(([, v]) => v)
      .map(([k]) => ({ key: k, label: foundationLabels[k] || k }));
  }, [data, foundationLabels]);

  // "proposal" (exact, from the linked quote) or "commitments" (inferred from
  // the PDF-parsed rows). Older payloads carry neither.
  const foundationSource = data?.foundations?.source || null;

  if (!programId) return null;

  return (
    <div className={`rounded-xl border border-border bg-card overflow-hidden ${compact ? "" : "shadow-none"}`} data-testid="smart-upsell-panel">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border bg-secondary/30 flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5">
          <div className="w-7 h-7 rounded-md bg-foreground text-background flex items-center justify-center shrink-0">
            <Sparkles className="w-3.5 h-3.5" />
          </div>
          <div className="min-w-0">
            <div className="text-[13.5px] font-semibold tracking-tight text-foreground">Smart Upsell Opportunities</div>
            <div className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
              {programName ? <>Ranked recommendations for <span className="text-foreground">{programName}</span> based on existing production foundations.</>
                            : "Ranked recommendations based on existing production foundations."}
            </div>
            {/* Where the scope came from. A project reading from "commitments" is
                inferring foundations from PDF-parsed prose, which is weaker and
                often empty — so say so, and say what fixes it. */}
            {foundationSource === "commitments" && (
              <div className="text-[10.5px] text-[#B96A43] mt-1 leading-relaxed" data-testid="foundations-source-warning">
                Scope inferred from older commitment records — link this project's proposal for exact scope.
              </div>
            )}
            {foundationSource === "proposal" && (
              <div className="text-[10.5px] text-muted-foreground/80 mt-1" data-testid="foundations-source">
                Scope read from the linked proposal.
              </div>
            )}
          </div>
        </div>
        {activeFoundations.length > 0 && (
          <button
            onClick={() => setShowFoundations(v => !v)}
            className="text-[10.5px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1 shrink-0"
            data-testid="toggle-foundations"
          >
            <Layers className="w-3 h-3" />
            {activeFoundations.length} foundation{activeFoundations.length !== 1 ? "s" : ""}
            {showFoundations ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        )}
      </div>

      {/* Foundation chips (collapsed by default) */}
      {showFoundations && activeFoundations.length > 0 && (
        <div className="px-4 py-2.5 border-b border-border bg-secondary/40">
          <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-medium mb-1.5">Detected foundations</div>
          <div className="flex flex-wrap gap-1">
            {activeFoundations.map(f => (
              <span key={f.key} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-card border border-border text-[10.5px] text-foreground">
                <CheckCircle2 className="w-2.5 h-2.5 text-emerald-600" /> {f.label}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Body */}
      <div className="p-3 space-y-2 max-h-[640px] overflow-y-auto">
        {loading && (
          <div className="space-y-2">
            {[0,1,2].map(i => <div key={i} className="h-20 rounded-lg bg-secondary animate-pulse" />)}
          </div>
        )}
        {error && (
          <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-900 flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {error}
          </div>
        )}
        {!loading && !error && (data?.recommendations || []).length === 0 && (
          <div className="text-center py-8 text-[12px] text-muted-foreground/70 italic">
            No recommendations available yet. Add deliverables or approve scope to unlock intelligent upsells.
          </div>
        )}
        {!loading && !error && (data?.recommendations || []).map(rec => (
          <RecommendationCard
            key={rec.rule_id}
            rec={rec}
            programId={programId}
            onAddToProposal={onAddToProposal}
            compact={compact}
          />
        ))}
      </div>
    </div>
  );
}

function RecommendationCard({ rec, programId, onAddToProposal, compact }) {
  const [pitchOpen, setPitchOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [customPrice, setCustomPrice] = useState(rec.recommended_price);
  const [customDiscountPct, setCustomDiscountPct] = useState(rec.discount_pct);

  const scoreColor =
    rec.priority_score >= 85 ? "bg-emerald-600"
    : rec.priority_score >= 70 ? "bg-blue-600"
    : rec.priority_score >= 50 ? "bg-amber-500"
    : "bg-muted-foreground";

  const handleCreateProposal = () => {
    if (onAddToProposal) {
      onAddToProposal({
        category: rec.category,
        item_name: rec.name,
        quantity: 1,
        unit_type: "feature",
        unit_price: customPrice,
        total_price: customPrice,
        pricing_key: rec.rule_id,
        description: rec.pitch_angle,
      });
      toast.success(`Added "${rec.name}" to the upsell scope`);
    } else {
      toast.message("Open this from the Upsell wizard to auto-fill the proposal.");
    }
  };

  return (
    <div className="rounded-lg border border-border bg-card hover:border-muted-foreground/30 transition-colors p-3" data-testid={`rec-${rec.rule_id}`}>
      {/* Top row — score + name + pricing */}
      <div className="flex items-start justify-between gap-3 mb-1.5">
        <div className="flex items-start gap-2.5 min-w-0">
          <div className={`shrink-0 w-9 h-9 rounded-md ${scoreColor} text-white flex flex-col items-center justify-center font-semibold tracking-tight leading-none`}>
            <span className="text-[13px]">{rec.priority_score}</span>
            <span className="text-[7px] uppercase opacity-80 mt-0.5">score</span>
          </div>
          <div className="min-w-0">
            <div className="text-[13px] font-semibold tracking-tight text-foreground leading-tight">{rec.name}</div>
            <div className="text-[10.5px] text-muted-foreground mt-0.5 flex items-center gap-1.5">
              <span>{rec.category}</span>
              {rec.foundation_complete ? (
                <Badge variant="outline" className="text-[9px] h-4 px-1 bg-emerald-50 text-emerald-700 border-emerald-200">Foundation ready</Badge>
              ) : (
                <Badge variant="outline" className="text-[9px] h-4 px-1 bg-amber-50 text-amber-700 border-amber-200">Bundling needed</Badge>
              )}
            </div>
          </div>
        </div>
        {/* Pricing — hidden entirely for users without financial access (backend
            returns null prices + pricing_redacted for PMs). */}
        {rec.recommended_price != null && (
          <div className="text-right shrink-0">
            {rec.discount_amount > 0 && (
              <div className="text-[10px] text-muted-foreground/70 line-through tabular-nums">{fmt(rec.standard_price)}</div>
            )}
            <div className="text-[14.5px] font-semibold tabular-nums text-foreground leading-tight">{fmt(rec.recommended_price)}</div>
            {rec.discount_amount > 0 && (
              <div className="text-[10px] text-emerald-700 font-medium">Save {fmt(rec.discount_amount)} ({rec.discount_pct.toFixed(0)}%)</div>
            )}
          </div>
        )}
      </div>

      {/* Pitch angle */}
      <p className="text-[11.5px] text-muted-foreground leading-snug mt-2">{rec.pitch_angle}</p>

      {/* Discount reason */}
      {rec.discount_reason && (
        <div className="mt-2 px-2.5 py-1.5 rounded bg-emerald-50/60 border border-emerald-100 text-[10.5px] text-emerald-900 leading-snug">
          <strong className="font-semibold">Why the discount: </strong>{rec.discount_reason}
        </div>
      )}

      {/* Warnings */}
      {(rec.warnings || []).map((w, i) => (
        <div key={i} className="mt-2 px-2.5 py-1.5 rounded bg-amber-50/60 border border-amber-100 text-[10.5px] text-amber-900 leading-snug flex items-start gap-1.5">
          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> {w}
        </div>
      ))}

      {/* Confidence chips */}
      <div className="mt-2 flex flex-wrap items-center gap-1 text-[10px] text-muted-foreground">
        <ConfidenceChip icon={ShieldCheck} label="Margin"   value={rec.margin_confidence} />
        <ConfidenceChip icon={Wand2}       label="Ease"     value={rec.ease_of_execution} />
        <ConfidenceChip icon={TrendingUp}  label="Value"    value={rec.client_sales_value} />
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">
          <Clock className="w-2.5 h-2.5" /> {rec.timeline_impact} impact
        </span>
      </div>

      {/* Actions */}
      {!compact && (
        <div className="mt-3 flex items-center gap-1.5 flex-wrap">
          <Button size="sm" onClick={handleCreateProposal} className="h-7 text-[11px] bg-foreground hover:bg-foreground/90 text-background gap-1" data-testid={`rec-add-${rec.rule_id}`}>
            <FileText className="w-3 h-3" /> {onAddToProposal ? "Add to Proposal" : "Create Upsell Proposal"}
          </Button>
          {/* Pitch + pricing tools require financial access — hidden for PMs. */}
          {rec.recommended_price != null && (
            <>
              <Button size="sm" variant="outline" onClick={() => setPitchOpen(true)} className="h-7 text-[11px] border-border gap-1" data-testid={`rec-pitch-${rec.rule_id}`}>
                <Sparkles className="w-3 h-3" /> Generate Client Pitch
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setAdjustOpen(v => !v)} className="h-7 text-[11px] text-muted-foreground gap-1">
                <DollarSign className="w-3 h-3" /> Adjust Pricing
              </Button>
            </>
          )}
        </div>
      )}

      {/* Pricing override (collapsible) */}
      {adjustOpen && !compact && (
        <div className="mt-2 pt-2 border-t border-border grid grid-cols-2 gap-2">
          <div>
            <label className="text-[9.5px] uppercase tracking-[0.1em] text-muted-foreground font-medium block mb-0.5">Final Price</label>
            <Input type="number" min="0" step="100" value={customPrice} onChange={(e) => setCustomPrice(Number(e.target.value))} className="h-7 text-[11.5px]" />
          </div>
          <div>
            <label className="text-[9.5px] uppercase tracking-[0.1em] text-muted-foreground font-medium block mb-0.5">Discount %</label>
            <Input type="number" min="0" max="60" step="1" value={customDiscountPct} onChange={(e) => setCustomDiscountPct(Number(e.target.value))} className="h-7 text-[11.5px]" />
          </div>
        </div>
      )}

      {pitchOpen && (
        <PitchModal rec={rec} programId={programId} customPrice={customPrice} customDiscountPct={customDiscountPct} onClose={() => setPitchOpen(false)} />
      )}
    </div>
  );
}

function ConfidenceChip({ icon: Icon, label, value }) {
  const pct = Math.round((value || 0) * 100);
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">
      <Icon className="w-2.5 h-2.5" /> {label} {pct}%
    </span>
  );
}

function PitchModal({ rec, programId, customPrice, customDiscountPct, onClose }) {
  const [pitch, setPitch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [audience, setAudience] = useState("developer");
  const [error, setError] = useState(null);

  const generate = async (aud = audience) => {
    setLoading(true);
    setError(null);
    try {
      const r = await apiClient.post("/upsell-intelligence/pitch", {
        rule_id: rec.rule_id,
        program_id: programId,
        custom_price: customPrice,
        custom_discount_pct: customDiscountPct,
        audience: aud,
      });
      setPitch(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { generate(); /* eslint-disable-next-line */ }, []);

  const copyPitch = async () => {
    if (!pitch?.pitch) return;
    try {
      await navigator.clipboard.writeText(pitch.pitch);
      toast.success("Pitch copied to clipboard");
    } catch {
      toast.error("Couldn't access clipboard");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card rounded-xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <div>
            <div className="text-[13px] font-semibold tracking-tight text-foreground">Client Pitch · {rec.name}</div>
            <div className="text-[10.5px] text-muted-foreground mt-0.5">AI-generated · Claude Haiku</div>
          </div>
          <button onClick={onClose} className="text-muted-foreground/70 hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>

        {/* Audience toggle */}
        <div className="px-5 py-2.5 border-b border-border bg-secondary/40 flex items-center gap-2">
          <span className="text-[10.5px] text-muted-foreground uppercase tracking-[0.1em] font-medium">Audience</span>
          {["developer", "broker", "investor"].map(a => (
            <button
              key={a}
              onClick={() => { setAudience(a); generate(a); }}
              className={`px-2 py-0.5 rounded text-[11px] font-medium transition-colors capitalize ${audience === a ? "bg-foreground text-background" : "text-muted-foreground hover:bg-secondary"}`}
            >
              {a}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-1.5">
            <Button size="sm" variant="outline" onClick={copyPitch} disabled={loading || !pitch} className="h-7 text-[11px] gap-1 border-border">
              <Copy className="w-3 h-3" /> Copy
            </Button>
            <Button size="sm" variant="outline" onClick={() => generate(audience)} disabled={loading} className="h-7 text-[11px] gap-1 border-border">
              <Sparkles className="w-3 h-3" /> {loading ? "Generating…" : "Regenerate"}
            </Button>
          </div>
        </div>

        <div className="px-5 py-4 overflow-y-auto flex-1">
          {loading && <div className="space-y-2"><div className="h-3 bg-secondary rounded animate-pulse w-full" /><div className="h-3 bg-secondary rounded animate-pulse w-4/5" /><div className="h-3 bg-secondary rounded animate-pulse w-3/4" /></div>}
          {error && <div className="text-[12px] text-rose-700">{error}</div>}
          {!loading && pitch && (
            <>
              <div className="text-[10.5px] uppercase tracking-[0.12em] text-muted-foreground font-medium mb-1">Recommended price</div>
              <div className="flex items-baseline gap-2 mb-4">
                <span className="text-[24px] font-light tabular-nums tracking-tight text-foreground">{fmt(pitch.recommended_price)}</span>
                {pitch.discount_pct > 0 && <span className="text-[12px] text-muted-foreground/70 line-through tabular-nums">{fmt(pitch.standard_price)}</span>}
              </div>
              <div className="text-[13px] text-foreground leading-relaxed whitespace-pre-wrap">{pitch.pitch}</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
